/**
 * @fileoverview Server API around managing devices and tags.
 *
 * NOTE: This covers the data model that the server API presents only. It doesn't handle converting
 * things to and from the model used within the client application.
 */

import { JTDDataType } from 'ajv/dist/jtd';
import { validate } from './validation';

export const DeviceTypes = [
  "UNKNOWN",
  "OPTO_CONTROLLER",
  "OPC_UA_SERVER",
  "REST_SERVER",
  "MODBUS_SERVER",
  "SIMULATOR",
  "CODESYS",
  "GROOV_SYSTEM",
  "DATA_STORE",
  "MMP",
  "EVENTS",
  "COMPUTED_TAGS"
];

export const StringEncodings = [
  "UTF_8",
  "ISO_LATIN_1",
  "SHIFT_JIS",
  "ISO_2022_JP",
  "EUC",
  "SHIFT_JIS_X0213",
  "BIG_5",
  "BIG_5_HKSCS",
  "CHINESE_WINDOWS",
  "ISO_2022_KR",
  "KOREAN_WINDOWS",
  "ISO_8859_6",
  "ARABIC_WINDOWS",
  "ISO_8859_8",
  "HEBREW_WINDOWS",
  "ISO_8859_7",
  "GREEK_WINDOWS",
  "ISO_8859_5",
  "CYRILLIC_KOI8_R",
  "CYRILLIC_WINDOWS",
  "UKRAINIAN_KOI8_U",
  "THAI",
  "CHINESE_GB_2312",
  "CHINESE_GB_18030",
  "ISO_LATIN_2",
  "WINDOWS_LATIN_2",
  "VIETNAMESE",
  "ISO_LATIN_5",
  "WINDOWS_LATIN_5",
  "ISO_LATIN_4",
  "BALTIC"
];

export interface DeviceBase {
  deviceType: string;
  deviceId: number;
  deviceEnabled: boolean;
  deviceName?: string;
}

// The DeviceBase required and optional properties are broken out into separate objects so I
// can easily include them in the more specific device schemas.
const DeviceBaseRequiredProperties: any = {
  deviceType:    { enum: DeviceTypes },
  deviceId:      { type: 'int32' },
  deviceEnabled: { type: 'boolean' }
};

// I don't like this being optional, but Opto Controllers have a stategyName field instead
// of a deviceName field.
const DeviceBaseOptionalProperties: any = {
  deviceName:    { type: 'string' }
}

export const DeviceBaseSchema: JTDDataType<DeviceBase> = {
  properties: DeviceBaseRequiredProperties,
  optionalProperties: DeviceBaseOptionalProperties,
  additionalProperties: true
};

export const DeviceBaseArraySchema: JTDDataType<DeviceBase[]> = {
  elements: DeviceBaseSchema
};

export interface ControllerDevice extends DeviceBase {
  ipAddress: string;
  port: number;
  strategyName: string;
  strategyFile: string;
  stringEncoding: string;
  timeout: number;
  scanInterval: number;
}

export const ControllerDeviceSchema: JTDDataType<ControllerDevice> = {
  properties: {
    ...DeviceBaseRequiredProperties,
    ipAddress:      { type: 'string' },
    port:           { type: 'int32' },
    strategyName:   { type: 'string' },
    strategyFile:   { type: 'string' },
    stringEncoding: { enum: StringEncodings },
    timeout:        { type: 'int32' },
    scanInterval:   { type: 'int32'}
  },
  additionalProperties: true
};

export interface OpcUaDevice extends DeviceBase {
  ipAddress: string;
  port: number;
  serverPath: string;
  securityPolicyName: string;
  messageSecurityModeName: string;
  userTokenTypeName: string;
  timeout: number;
  scanInterval: number;
};

export const OpcUaDeviceSchema: JTDDataType<OpcUaDevice> = {
  properties: {
    ...DeviceBaseRequiredProperties,
    ...DeviceBaseOptionalProperties,
    ipAddress:      { type: 'string' },
    port:           { type: 'int32' },
    serverPath:     { type: 'string' },
    securityPolicyName:      { type: 'string' }, // TODO enum
    messageSecurityModeName: { type: 'string' }, // TODO enum
    userTokenTypeName:       { type: 'string' }, // TODO enum
    timeout:        { type: 'int32' },
    scanInterval:   { type: 'int32' }
  },
  additionalProperties: true
};

export async function fetchDevices(): Promise<DeviceBase[]> {
  return fetch('api/v0/devices.json', { cache: 'no-cache' })
    .then(response => {
      if (!response.ok) {
        return Promise.reject();
      }

      return response.json();
    })
    .then(json => validate(DeviceBaseArraySchema, json));
}

/*
 (def ModbusWordOrder
   [:enum "AB" "BA"])

 (def ModbusDevice
   [:map
    ["ipAddress" :string]
    ["port"      :int]
    ["unitId"    :int]
    ["useOneBasedAddressing"          :boolean]
    ["useSingleCoilWriteFunction"     :boolean]
    ["useSingleRegisterWriteFunction" :boolean]
    ["useRegisterMaskWriteFunction"   :boolean]
    ["wordOrder"      #'ModbusWordOrder]
    ["dwordOrder"     #'ModbusWordOrder]
    ["stringEncoding" #'StringEncoding]
    ["timeout"        :int]
    ["scanInterval"   :int]])

 (def MmpDevice
   [:map
    ["ipAddress"      :string]
    ["port"           :int]
    ["local"          :boolean]
    ["stringEncoding" #'StringEncoding]
    ["timeout"        :int]
    ["scanInterval"   :int]])
 */